import { Suspense } from "react";
import { BrowserRouter, Switch, Route } from "react-router-dom";
import { Provider } from "react-redux";

import { RepresentativeRoute } from "../RepresentativeRoute";
import { WfpManagerRoute } from "../WfpManagerRoute";
import configureStore from "../../store";
import ErrorMessage from "../ErrorMessage";
import MarketOfferView from "../MarketOfferView";
import { ConnectedMarketOfferDetails } from "../MarketOfferDetails";
import MarketOfferSms from "../MarketOfferSms";
import { Login } from "../Login";
import { Logout } from "../Logout";
import { LoginCallback } from "../LoginCallback";
import { LogoutCallback } from "../LogoutCallback";
import { LoginRedirect } from "../LoginRedirect";
import LoginForbidden from "../LoginForbidden";
import LoginForbiddenAdministrator from "../LoginForbiddenAdministrator";
import { NotFound } from "../NotFound";
import {
  ConnectedRepresentativeOffers as RepresentativeOffers,
  WfpManagerOffers,
} from "../RepresentativeOffers";
import { RepresentativeHome } from "../RepresentativeHome";
import { ConnectedBidRequests, WfpManagerBidRequests } from "../BidRequests";
import { ConnectedBidRequestCreate } from "../BidRequestCreate";
import { ConnectedBidRequestDetails, WfpManagerBidRequestDetails } from "../BidRequestDetails";
import { WfpManagerHomeConnected as WfpManagerHome } from "../WfpManagerHome";
import { AggregatorAdministrators } from "../AggregatorAdministrators";
import { BuyerRepresentatives } from "../BuyerRepresentatives";
import { Farmers } from "../Farmers";
import Aggregators from "../Aggregators";
import AggregatorProfile from "../AggregatorProfile";
import { ConnectedDeposits, WfpManagerDeposits } from "../Deposits";
import { DepositDetails, WfpManagerDepositDetails } from "../Deposits/DepositDetails";
import RepresentativeAggregatorCreationRequests from "../RepresentativeAggregatorCreationRequests";
import RepresentativeAggregatorCreationRequestView from "../RepresentativeAggregatorCreationRequestView";
import { ConnectedRepresentativeAggregatorCreationRequestDetails } from "../RepresentativeAggregatorCreationRequestDetails";
import WfpManagerAggregatorCreationRequests from "../WfpManagerAggregatorCreationRequests";
import WfpManagerAggregatorCreationRequestView from "../WfpManagerAggregatorCreationRequestView";
import Traders from "../Traders";
import WfpManagerAggregatorView from "../WfpManagerAggregatorView";
import RepresentativeAggregatorView from "../RepresentativeAggregatorView";
import TraderDetails from "../TraderDetails";
import AggregatorDetails from "../AggregatorDetails";
import { ConnectedCommodities as Commodities } from "../Commodities";
import { UpdateApplicationBanner } from "../UpdateApplicationBanner";
import { ConnectedPlatformAccess } from "../PlatformAccess";
import CommodityEdit from "../CommodityEdit";
import { ConnectedManagers } from "../Managers";
import { ConnectedProfile as Profile } from "../Profile";
import Configuration from "../WfpManagerConfigurationView";
import { ConnectedWfpManagerAdministrativeAreaTypesView as AdministrativeAreaTypes } from "../WfpManagerAdministrativeAreaTypesView";
import WfpManagerCommodityUnitsOfMeasureView from "../WfpManagerCommodityUnitsOfMeasureView";
import { ConnectedWfpManagerAdministrativeAreasView as WfpManagerAdministrativeAreasView } from "../WfpManagerAdministrativeAreasView";
import { ConnectedQualityGrades as QualityGrades } from "../QualityGrades";
import { ConnectedDepositDeleteReasons as DepositDeleteReasons } from "../DepositDeleteReasons";
import { ConnectedWfpManagerExchangeLocationsView } from "../WfpManagerExchangeLocationsView";
import { ConnectedFarmerDeleteReasons as FarmerDeleteReasons } from "../FarmerDeleteReasons";
import { ConnectedWfpManagerFarmerIdentificationView } from "../WfpManagerFarmerIdentificationView";
import WfpManagerLandUnitsOfMeasureView from "../WfpManagerLandUnitsOfMeasureView";
import { ConnectedPaymentMethods as PaymentMethods } from "../PaymentMethods";
import WfpManagerRecallReasonsView from "../WfpManagerRecallReasonsView";
import WfpManagerTraderMarketOfferNegotiationProposalReasonsView from "../WfpManagerTraderMarketOfferNegotiationProposalReasonsView";
import { ConnectedAggregatorOfferRejectionReasons as AggregatorOfferRejectionReasons } from "../AggregatorOfferRejectionReasons";
import WfpManagerSystemNotifications from "../WfpManagerSystemNotifications";
import WfpManagerAggregatorUmbrellaOrganizations from "../WfpManagerAggregatorUmbrellaOrganizations";
import InfoHub from "../InfoHub";
import { EnvironmentIndicator } from "../EnvironmentIndicator";
import { ConnectedWfpManagerTermsAndConditionsText as WfpManagerTermsAndConditionsText } from "../WfpManagerTermsAndConditionsText";
import { InfoHubForm } from "../InfoHub/Components/Form/InfoHubFormWrapper";
import { ConnectedExternalUsers } from "../ExternalUsers";
import { GuestRoute } from "../GuestRoute";
import { LoginForbiddenGuest } from "../LoginForbiddenGuest";
import { AccountDeletion } from "../AccountDeletion";
import { AccountDeletionRoute } from "../AccountDeletionRoute";
import { ACCOUNT_DELETION_PATH } from "../../constants";
import LoggedOutRoute from "../LoggedOutRoute";
import AccountDeletionConfirmation from "../AccountDeletion/AccountDeletionConfirmation";
import { AccountDeletionVerification } from "../AccountDeletion/AccountDeletionVerification";
import AccountDeletionFeedback from "../AccountDeletion/AccountDeletionFeedback";
import { MCFAdminRoute } from "../MCFAdminRoute";
import MCFAdminHome from "../MCFAdminHome";
import { FullscreenLoading } from "../../components/FullscreenLoading";
// Through the store we will share redux state to all component used in the application
const store = configureStore();

// The application root component is created, the Provider allow to share
// the redux store with all components used in the application
// and the BrowserRouter allow user to navigate through all the pages
export const Application = () => {
  return (
    <Provider store={store}>
      <Suspense fallback={<FullscreenLoading isVisible />}>
        <BrowserRouter>
          <UpdateApplicationBanner />
          <EnvironmentIndicator />
          <Switch>
            <LoggedOutRoute
              path="/accountDeletion/verify/:uuid"
              component={AccountDeletionVerification}
            />
            <LoggedOutRoute path="/accountDeletion/feedback" component={AccountDeletionFeedback} />
            <LoggedOutRoute
              path="/accountDeletion/confirmation"
              component={AccountDeletionConfirmation}
            />
            <AccountDeletionRoute path={ACCOUNT_DELETION_PATH} component={AccountDeletion} />
            <Route exact path="/login" component={Login} />
            <Route exact path="/logout" component={Logout} />
            <Route exact path="/errorMessage" component={ErrorMessage} />
            <Route exact path="/loginForbidden" component={LoginForbidden} />
            <Route
              exact
              path="/loginForbiddenAdministrator"
              component={LoginForbiddenAdministrator}
            />
            <Route exact path="/loginForbiddenGuest" component={LoginForbiddenGuest} />
            <Route exact path="/ciam_callback/" component={LoginCallback} />
            <Route exact path="/ciam_logout_callback/" component={LogoutCallback} />
            <Route exact path="/" component={LoginRedirect} />

            <RepresentativeRoute exact path="/representative/" component={RepresentativeHome} />
            <RepresentativeRoute
              exact
              path="/representative/offer"
              component={RepresentativeOffers}
            />
            <RepresentativeRoute
              exact
              path="/representative/marketOfferView/:id"
              component={MarketOfferView}
            />
            <RepresentativeRoute
              exact
              path="/representative/marketOfferSms/:id"
              component={MarketOfferSms}
            />
            <RepresentativeRoute
              exact
              path="/representative/marketOfferDetails"
              component={ConnectedMarketOfferDetails}
            />
            <RepresentativeRoute
              exact
              path="/representative/representativeAggregatorCreationRequests"
              component={RepresentativeAggregatorCreationRequests}
            />
            <RepresentativeRoute
              exact
              path="/representative/representativeAggregatorCreationRequestView/:id"
              component={RepresentativeAggregatorCreationRequestView}
            />
            <RepresentativeRoute
              exact
              path="/representative/representativeAggregatorCreationRequestDetails"
              component={ConnectedRepresentativeAggregatorCreationRequestDetails}
            />
            <RepresentativeRoute
              exact
              path="/representative/representativeAggregatorCreationRequestDetails/:id"
              component={ConnectedRepresentativeAggregatorCreationRequestDetails}
            />
            <RepresentativeRoute
              exact
              path="/representative/representativeAggregatorView/:id"
              component={RepresentativeAggregatorView}
            />
            <RepresentativeRoute
              exact
              path="/representative/market"
              component={ConnectedDeposits}
            />
            <RepresentativeRoute
              exact
              path="/representative/market/aggregator/:id"
              component={AggregatorProfile}
            />
            <RepresentativeRoute
              exact
              path="/representative/market/deposit/:uuid"
              component={DepositDetails}
            />
            <RepresentativeRoute
              exact
              path="/representative/mobile"
              component={RepresentativeOffers}
              emptyLayout
            />
            <RepresentativeRoute
              exact
              path="/representative/bidRequests"
              component={ConnectedBidRequests}
            />
            <RepresentativeRoute
              exact
              path="/representative/bidRequests/new"
              component={ConnectedBidRequestCreate}
            />
            <RepresentativeRoute
              exact
              path="/representative/bidRequests/:id"
              component={ConnectedBidRequestDetails}
            />
            <RepresentativeRoute exact path="/representative/profile" component={Profile} />
            <WfpManagerRoute exact path="/wfpManager/managers" component={ConnectedManagers} />
            <WfpManagerRoute
              exact
              path="/wfpManager/externalUsers"
              component={ConnectedExternalUsers}
            />
            <WfpManagerRoute exact path="/wfpManager" component={WfpManagerHome} />
            <WfpManagerRoute
              exact
              path="/wfpManager/administrators"
              component={AggregatorAdministrators}
            />
            <WfpManagerRoute
              exact
              path="/wfpManager/buyerRepresentatives"
              component={BuyerRepresentatives}
            />
            <WfpManagerRoute exact path="/wfpManager/farmers" component={Farmers} />
            <WfpManagerRoute exact path="/wfpManager/offers" component={WfpManagerOffers} />
            <WfpManagerRoute
              exact
              path="/wfpManager/bidRequests"
              component={WfpManagerBidRequests}
            />
            <WfpManagerRoute
              exact
              path="/wfpManager/bidRequests/:id"
              component={WfpManagerBidRequestDetails}
            />
            <WfpManagerRoute exact path="/wfpManager/market" component={WfpManagerDeposits} />
            <WfpManagerRoute
              exact
              path="/wfpManager/market/deposit/:uuid"
              component={WfpManagerDepositDetails}
            />
            <MCFAdminRoute exact path="/stats/" component={MCFAdminHome} />
            <GuestRoute exact path="/guest/" component={WfpManagerHome} />
            <WfpManagerRoute
              exact
              path="/wfpManager/marketOfferView/:id"
              component={MarketOfferView}
            />
            <WfpManagerRoute exact path="/wfpManager/aggregators" component={Aggregators} />
            <WfpManagerRoute
              exact
              path="/wfpManager/aggregators/:id"
              component={AggregatorProfile}
            />
            <WfpManagerRoute
              exact
              path="/wfpManager/wfpManagerAggregatorView/:id"
              component={WfpManagerAggregatorView}
            />
            <WfpManagerRoute
              exact
              path="/wfpManager/aggregatorDetails"
              component={AggregatorDetails}
              disallowExternalUser
              externalUserRedirectPath="/wfpManager/aggregators"
            />
            <WfpManagerRoute
              exact
              path="/wfpManager/aggregatorDetails/:id"
              component={AggregatorDetails}
              disallowExternalUser
              externalUserRedirectPath="/wfpManager/aggregators"
            />
            <WfpManagerRoute exact path="/wfpManager/traders" component={Traders} />
            <WfpManagerRoute exact path="/wfpManager/commodities" component={Commodities} />
            <WfpManagerRoute
              exact
              path="/wfpManager/commodities/:id"
              component={CommodityEdit}
              disallowExternalUser
              externalUserRedirectPath="/wfpManager/commodities"
            />
            <WfpManagerRoute
              exact
              path="/wfpManager/traderDetails"
              component={TraderDetails}
              disallowExternalUser
              externalUserRedirectPath="/wfpManager/traders"
            />
            <WfpManagerRoute
              exact
              path="/wfpManager/traderDetails/:id"
              component={TraderDetails}
              disallowExternalUser
              externalUserRedirectPath="/wfpManager/traders"
            />
            <WfpManagerRoute
              exact
              path="/wfpManager/wfpManagerAggregatorCreationRequests"
              component={WfpManagerAggregatorCreationRequests}
            />
            <WfpManagerRoute
              exact
              path="/wfpManager/wfpManagerAggregatorCreationRequestView/:id"
              component={WfpManagerAggregatorCreationRequestView}
            />
            <WfpManagerRoute
              exact
              path="/systemAdmin/platformAccess"
              component={ConnectedPlatformAccess}
            />
            <WfpManagerRoute exact path="/wfpManager/configuration" component={Configuration} />
            <WfpManagerRoute
              exact
              path="/wfpManager/configuration/paymentMethods"
              component={PaymentMethods}
            />
            <WfpManagerRoute
              exact
              path="/wfpManager/configuration/farmerIdentification"
              component={ConnectedWfpManagerFarmerIdentificationView}
            />
            <WfpManagerRoute
              exact
              path="/wfpManager/configuration/farmerDeletionReasons"
              component={FarmerDeleteReasons}
            />
            <WfpManagerRoute
              exact
              path="/wfpManager/configuration/depositDeletionReasons"
              component={DepositDeleteReasons}
            />
            <WfpManagerRoute
              exact
              path="/wfpManager/configuration/aggregatorOfferRejectionReasons"
              component={AggregatorOfferRejectionReasons}
            />
            <WfpManagerRoute
              exact
              path="/wfpManager/configuration/recallReasons"
              component={WfpManagerRecallReasonsView}
            />
            <WfpManagerRoute
              exact
              path="/wfpManager/configuration/exchangeLocations"
              component={ConnectedWfpManagerExchangeLocationsView}
            />
            <WfpManagerRoute
              exact
              path="/wfpManager/configuration/commodityQualityGrades"
              component={QualityGrades}
            />
            <WfpManagerRoute
              exact
              path="/wfpManager/configuration/administrativeAreas"
              component={WfpManagerAdministrativeAreasView}
            />
            <WfpManagerRoute
              exact
              path="/wfpManager/configuration/administrativeAreaTypes"
              component={AdministrativeAreaTypes}
            />
            <WfpManagerRoute
              exact
              path="/wfpManager/configuration/traderMarketOfferNegotiationProposalReasons"
              component={WfpManagerTraderMarketOfferNegotiationProposalReasonsView}
            />
            <WfpManagerRoute
              exact
              path="/wfpManager/configuration/landUnitsOfMeasure"
              component={WfpManagerLandUnitsOfMeasureView}
            />
            <WfpManagerRoute
              exact
              path="/wfpManager/configuration/commodityUnitsOfMeasure"
              component={WfpManagerCommodityUnitsOfMeasureView}
            />
            <WfpManagerRoute
              exact
              path="/wfpManager/configuration/systemNotifications"
              component={WfpManagerSystemNotifications}
            />
            <WfpManagerRoute
              exact
              path="/wfpManager/configuration/aggregatorUmbrellaOrganizations"
              component={WfpManagerAggregatorUmbrellaOrganizations}
            />
            <WfpManagerRoute
              exact
              path="/wfpManager/configuration/termsAndConditionsText"
              component={WfpManagerTermsAndConditionsText}
            />
            <WfpManagerRoute exact path="/wfpManager/infoHub" component={InfoHub} />
            <WfpManagerRoute exact path="/wfpManager/infohub/newContent" component={InfoHubForm} />

            <WfpManagerRoute
              exact
              path="/wfpManager/infohub/newContent/:id"
              component={InfoHubForm}
            />
            <Route component={NotFound} />
          </Switch>
        </BrowserRouter>
      </Suspense>
    </Provider>
  );
};
